import * as React from "react";

import { MailboxItemSnapshot } from "./models";

function readSnapshot(): MailboxItemSnapshot {
  const item = Office.context.mailbox.item as Office.MessageRead | undefined;
  return {
    itemId: (item?.itemId as string) ?? "",
    subject: item && typeof item.subject === "string" ? item.subject : "",
    senderName: item?.from?.displayName ?? "",
    attachments: item && Array.isArray(item.attachments) ? item.attachments : [],
  };
}

const MailboxContext = React.createContext<MailboxItemSnapshot | null>(null);

export const MailboxProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [snapshot, setSnapshot] = React.useState<MailboxItemSnapshot>(readSnapshot);

  React.useEffect(() => {
    const handleItemChanged = () => setSnapshot(readSnapshot());

    Office.context.mailbox.addHandlerAsync(Office.EventType.ItemChanged, handleItemChanged, (asyncResult) => {
      if (asyncResult.status === Office.AsyncResultStatus.Failed) {
        console.warn("ItemChanged handler registration failed:", asyncResult.error.message);
      }
    });

    Office.context.mailbox.addHandlerAsync(Office.EventType.SelectedItemsChanged, handleItemChanged, (asyncResult) => {
      if (asyncResult.status === Office.AsyncResultStatus.Failed) {
        console.warn("SelectedItemsChanged handler registration failed:", asyncResult.error.message);
      }
    });

    return () => {
      Office.context.mailbox.removeHandlerAsync(Office.EventType.ItemChanged, handleItemChanged);
      Office.context.mailbox.removeHandlerAsync(Office.EventType.SelectedItemsChanged, handleItemChanged);
    };
  }, []);

  return <MailboxContext.Provider value={snapshot}>{children}</MailboxContext.Provider>;
};

export function useMailboxItem(): MailboxItemSnapshot {
  const context = React.useContext(MailboxContext);
  if (!context) {
    throw new Error("useMailboxItem must be used within a MailboxProvider");
  }
  return context;
}
